import { useRef, useState } from "react";
import { api, ApiError } from "../api";

/**
 * ENVIAR UM DOCUMENTO QUE ESTÁ FALTANDO.
 *
 * O arquivo vai para o DP conferir; aprovado, entra na pasta oficial da pessoa
 * (a mesma que RH e DP usam). No celular, "Fotografar" abre a câmera direto —
 * é o jeito mais rápido de mandar um RG ou um comprovante de residência.
 */

interface Props {
  pastas: string[];
  onEnviado: (mensagem: string) => void;
  onCancelar: () => void;
}

const MAX_MB = 10;

function tamanho(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export default function EnviarDocumento({ pastas, onEnviado, onCancelar }: Props) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [pasta, setPasta] = useState(pastas[0] ?? "Documentos pessoais");
  const [descricao, setDescricao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const camera = useRef<HTMLInputElement>(null);
  const arquivos = useRef<HTMLInputElement>(null);

  function escolher(files: FileList | null) {
    const f = files?.[0] ?? null;
    setErro(null);
    if (f && f.size > MAX_MB * 1024 * 1024) {
      setErro(`Arquivo muito grande (${tamanho(f.size)}). O limite é ${MAX_MB} MB.`);
      return;
    }
    setArquivo(f);
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!arquivo) {
      setErro("Escolha o arquivo ou tire a foto do documento.");
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.pedirDocumento(arquivo, pasta, descricao.trim());
      onEnviado(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível enviar o documento.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="painel-form" onSubmit={enviar}>
      <p className="explica">
        Envie o documento que está faltando. Ele entra na sua pasta <strong>depois que o DP
        conferir</strong>. Aceita PDF, JPG, PNG e Word, até {MAX_MB} MB.
      </p>

      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => escolher(e.target.files)} />
      <input
        ref={arquivos}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png"
        hidden
        onChange={(e) => escolher(e.target.files)}
      />

      <div className="botoes">
        <button type="button" className="botao-secundario" onClick={() => camera.current?.click()}>
          Fotografar documento
        </button>
        <button type="button" className="botao-secundario" onClick={() => arquivos.current?.click()}>
          Escolher arquivo
        </button>
      </div>

      {arquivo && (
        <div className="arquivo-escolhido">
          <strong>{arquivo.name}</strong>
          <span>{tamanho(arquivo.size)}</span>
        </div>
      )}

      <label className="campo">
        <span>Que documento é?</span>
        <select value={pasta} onChange={(e) => setPasta(e.target.value)}>
          {pastas.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span>Descrição (opcional)</span>
        <input
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          maxLength={200}
          placeholder="Ex.: RG frente e verso, certidão do meu filho"
        />
      </label>

      {erro && <div className="aviso-erro">{erro}</div>}

      <div className="botoes">
        <button type="button" className="botao-texto" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </button>
        <button type="submit" className="botao-principal" disabled={!arquivo || enviando}>
          {enviando ? "Enviando…" : "Enviar para o DP"}
        </button>
      </div>
    </form>
  );
}
